import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import { Button, CircularProgress } from '@material-ui/core';
import { IoIosAttach } from 'react-icons/io';
import { AiOutlineDelete, AiOutlineUpload } from 'react-icons/ai';

import RequestField from '../request-field';
import {
  deleteAttachment,
  uploadAttachments,
} from '../../../../api/requests/attachment';
import { getErrorMessage } from '../../../../shared/services/error-handler';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_FORMATS = 'application/pdf, image/jpeg, image/png';

/**
 * Files are uploaded as soon as they are picked, so the user can add and
 * remove them while still filling the form. The ids collected here are sent
 * with the form and bound to the created record by the backend.
 */
const AttachmentsField = (props) => {
  const {
    entityType,
    attachments,
    setAttachments,
    tLabel,
    required,
    tTitle,
    tHint,
    tError,
    hasError,
  } = props;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length) return;

      const oversized = acceptedFiles.filter(
        (file) => file.size > MAX_FILE_SIZE
      );

      if (oversized.length) {
        enqueueSnackbar(t('attachment-too-large'), { variant: 'error' });
        return;
      }

      const formData = new FormData();
      formData.append('entity_type', entityType);
      acceptedFiles.forEach((file) =>
        formData.append('filesCollection', file)
      );

      setUploading(true);

      try {
        const uploaded = await uploadAttachments(formData);
        setAttachments((current) => [...current, ...uploaded]);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error) || t('something-went-wrong'), {
          variant: 'error',
        });
      } finally {
        setUploading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityType, setAttachments, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    onDrop,
    accept: ACCEPTED_FORMATS,
    disabled: uploading,
  });

  const onRemove = async (attachment) => {
    try {
      await deleteAttachment(attachment.id);
      setAttachments((current) =>
        current.filter((item) => item.id !== attachment.id)
      );
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error) || t('something-went-wrong'), {
        variant: 'error',
      });
    }
  };

  return (
    <RequestField
      tLabel={tLabel}
      required={required}
      tError={tError}
      hasError={hasError}
    >
      <div className="request-attachments">
        <div
          {...getRootProps({
            className: `request-dropzone ${isDragActive ? 'dragging' : ''}`,
          })}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <CircularProgress size={22} />
          ) : (
            <AiOutlineUpload size={22} />
          )}
          <p className="request-dropzone-title">
            {uploading ? t('uploading') : t(tTitle)}
          </p>
          <p className="request-dropzone-hint">{t(tHint)}</p>
        </div>

        {attachments.length > 0 && (
          <div className="request-attachment-list">
            {attachments.map((attachment) => (
              <div className="request-attachment-item" key={attachment.id}>
                <span className="request-attachment-name">
                  <IoIosAttach />
                  {attachment.file_name}
                </span>
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => onRemove(attachment)}
                  startIcon={<AiOutlineDelete />}
                >
                  {t('delete')}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </RequestField>
  );
};

export default AttachmentsField;
